import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
  total?: number;
}

export interface AgentTool {
  tool_id?: string;
  agent_tool_id?: string;
  name?: string;
  tool_name?: string;
  description?: string;
  argument_schema?: Record<string, unknown>;
  toolkit_name?: string;
  toolkit_type?: string;
  requires_confirmation?: boolean;
  action_card_template?: unknown;
  enabled_preview?: boolean;
  preview_tool?: unknown;
}

/** A point-in-time snapshot of an agent's prompt/config, persisted in the agent record. */
export interface AgentVersion {
  id: string;
  n: number;
  message: string;
  created_at: string;
  created_by?: string;
  snapshot: Record<string, unknown>;
}

export interface ModelsConfig {
  text: string;
  realtime?: string | null;
}

export interface Agent {
  agent_id: string;
  agent_name: string;
  description?: string;
  background?: string;
  role_of_the_agent?: string;
  company_information?: string;
  agent_response_structure?: string;
  models: ModelsConfig;
  status: string;
  /** "conversational" (chat-style) or "workflow" (trigger-driven automation) */
  agent_type?: 'conversational' | 'workflow';
  /** How a workflow agent is triggered */
  trigger_type?: 'manual' | 'schedule' | 'webhook' | 'event';
  /** Prompt/config version history (round-trips via the loose backend schema). */
  versions?: AgentVersion[];
  published_version_id?: string;
  /** Dotted context tokens auto-injected into the system prompt (e.g. "user.timezone"). */
  prompt_variables?: string[];
  tenant_id?: string | null;
  icon_url?: string;
  category?: string;
  tags?: string[];
  tools: AgentTool[];
  audio_prompt?: string;
  system_prompts: Array<{ id?: string; title?: string; prompt: string; order?: number }>;
  persistent_views: Record<string, unknown>[];
  data_store_config: Record<string, unknown>[];
  email_listeners: Record<string, unknown>[];
  model_parameters: Record<string, unknown>;
  voice: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export type AgentUpsert = Partial<Agent> & { agent_name: string; models: ModelsConfig };

export interface SupportedModel { model_id: string; name: string; description?: string }
export interface Toolkit { toolkit_id: string; toolkit_name: string; toolkit_type: string; description?: string }
export interface ParameterDescriptor {
  key: string; label: string; type: string; default?: unknown;
  min?: number; max?: number; options?: string[]; applies_to?: string;
}
export interface ParameterCatalogue { groups: Record<string, ParameterDescriptor[]> }

export interface AgentFile {
  file_id: string;
  name: string;
  url: string;
  type?: string;
  size?: number;
  file_type?: string;
  /** ingestion pipeline status — pending | processing | completed | failed */
  ingestion_status?: 'pending' | 'processing' | 'completed' | 'failed';
}

export interface AgentRun {
  run_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at?: string;
  ended_at?: string;
  /** Duration in seconds, if available from the API */
  duration_seconds?: number;
  trigger_type?: string;
}

interface ListAgentsArgs { search?: string; status?: string; model?: string; skip?: number; limit?: number }

export const agentsApi = createApi({
  reducerPath: 'agentsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Agent', 'AgentList'],
  endpoints: (builder) => ({
    listAgents: builder.query<{ data: Agent[]; total: number }, ListAgentsArgs | void>({
      query: (args) => {
        const p = new URLSearchParams();
        const a = args || {};
        if (a.search) p.set('search', a.search);
        if (a.status) p.set('status', a.status);
        if (a.model) p.set('model', a.model);
        if (a.skip != null) p.set('skip', String(a.skip));
        if (a.limit != null) p.set('limit', String(a.limit));
        const qs = p.toString();
        return `/api/v1/agents${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: ApiResponse<Agent[]>) => ({ data: res.data, total: res.total ?? res.data.length }),
      providesTags: ['AgentList'],
    }),
    getAgent: builder.query<Agent, string>({
      query: (id) => `/api/v1/agents/${id}`,
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Agent', id }],
    }),
    createAgent: builder.mutation<Agent, AgentUpsert>({
      query: (body) => ({ url: '/api/v1/agents', method: 'POST', body }),
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      invalidatesTags: ['AgentList'],
    }),
    updateAgent: builder.mutation<Agent, { id: string; body: AgentUpsert }>({
      query: ({ id, body }) => ({ url: `/api/v1/agents/${id}`, method: 'PUT', body }),
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      invalidatesTags: (_r, _e, { id }) => ['AgentList', { type: 'Agent', id }],
    }),
    setAgentStatus: builder.mutation<Agent, { id: string; status: string }>({
      query: ({ id, status }) => ({ url: `/api/v1/agents/${id}/status`, method: 'PATCH', body: { status } }),
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      invalidatesTags: ['AgentList'],
    }),
    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/agents/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AgentList'],
    }),
    cloneAgent: builder.mutation<Agent, { id: string; agent_name?: string }>({
      query: ({ id, agent_name }) => ({ url: `/api/v1/agents/${id}/copies`, method: 'POST', body: { agent_name } }),
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      invalidatesTags: ['AgentList'],
    }),
    listModels: builder.query<SupportedModel[], void>({
      query: () => '/api/v1/agents/models',
      transformResponse: (res: ApiResponse<SupportedModel[]>) => res.data,
    }),
    listToolkits: builder.query<Toolkit[], void>({
      query: () => '/api/v1/agents/toolkits',
      transformResponse: (res: ApiResponse<Toolkit[]>) => res.data,
    }),
    listTools: builder.query<AgentTool[], void>({
      query: () => '/api/v1/tools',
      transformResponse: (res: ApiResponse<AgentTool[]>) => res.data,
    }),
    getParameterCatalogue: builder.query<ParameterCatalogue, void>({
      query: () => '/api/v1/agents/parameter-catalogue',
      transformResponse: (res: ApiResponse<ParameterCatalogue>) => res.data,
    }),
    listAvailableAgents: builder.query<Agent[], void>({
      query: () => '/api/v1/agents/available',
      transformResponse: (res: ApiResponse<Agent[]>) => res.data,
    }),
    uploadAgentFiles: builder.mutation<AgentFile[], FormData>({
      query: (formData) => ({ url: '/api/v1/agents/upload', method: 'POST', body: formData }),
      transformResponse: (res: ApiResponse<AgentFile[]>) => res.data,
    }),
    listAgentRuns: builder.query<{ data: AgentRun[]; total: number }, string>({
      query: (id) => `/api/v1/agents/${id}/runs`,
      transformResponse: (res: ApiResponse<AgentRun[]>) => ({
        data: res.data,
        total: res.total ?? res.data.length,
      }),
      providesTags: (_r, _e, id) => [{ type: 'Agent', id: `${id}_runs` }],
    }),
    triggerAgentRun: builder.mutation<AgentRun, string>({
      query: (id) => ({ url: `/api/v1/agents/${id}/runs`, method: 'POST', body: {} }),
      transformResponse: (res: ApiResponse<AgentRun>) => res.data,
      invalidatesTags: (_r, _e, id) => [{ type: 'Agent', id: `${id}_runs` }],
    }),
    deleteAgentFile: builder.mutation<void, string>({
      query: (fileId) => ({ url: `/api/v1/agents/upload/${fileId}`, method: 'DELETE' }),
    }),
    previewAgentPrompt: builder.mutation<{ system_prompt: string }, { agent_id: string }>({
      query: (body) => ({ url: '/api/v1/agents/preview-prompt', method: 'POST', body }),
      transformResponse: (res: ApiResponse<{ system_prompt: string }>) => res.data,
    }),
  }),
});

export const {
  useListAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useSetAgentStatusMutation,
  useDeleteAgentMutation,
  useCloneAgentMutation,
  useListModelsQuery,
  useListToolkitsQuery,
  useListToolsQuery,
  useGetParameterCatalogueQuery,
  useListAvailableAgentsQuery,
  useUploadAgentFilesMutation,
  useListAgentRunsQuery,
  useTriggerAgentRunMutation,
  useDeleteAgentFileMutation,
  usePreviewAgentPromptMutation,
} = agentsApi;
